import { useEffect, useMemo, useState } from 'react';
import { ApiError, errorMessage, put } from '../../api/client';
import type { BikeModel, Media } from '../../api/types';
import { IconPlus, IconTrash } from '../../components/icons';
import { ImageSlot, ImageStrip, SpinPreview, type StripItem, toStrip } from '../../components/media';
import { DragHandle, SortableList } from '../../components/sortable';
import { Field, FieldAligned, slugify, useFeedback } from '../../components/ui';
import { SaveBar } from './SaveBar';
import { useDraft, useUnloadGuard } from '../../hooks';
import { useT } from '../../i18n';
import { LIMITS, type TabProps } from './shared';

interface ColorForm {
  uid: string;
  key: string;
  keyTouched: boolean;
  label: string;
  hex: string;
  sideRight: Media | null;
  sideLeft: Media | null;
  frames: StripItem[];
}

const fromModel = (m: BikeModel): ColorForm[] =>
  m.colors.map((c) => ({
    uid: c.id,
    key: c.key,
    keyTouched: true,
    label: c.label,
    hex: c.hex,
    sideRight: c.sideRight,
    sideLeft: c.sideLeft,
    frames: toStrip(c.frames),
  }));

const plain = (list: ColorForm[]) =>
  JSON.stringify(list.map((c) => [c.key, c.label, c.hex.toUpperCase(), c.sideRight?.id, c.sideLeft?.id, c.frames.map((f) => f.media.id)]));

const HEX = /^#[0-9A-Fa-f]{6}$/;

export function ColorsTab({ model, onSaved, onDirty }: TabProps) {
  const { t, tn } = useT();
  const initial = useMemo(() => fromModel(model), [model]);
  const { draft: colors, setDraft: setColors, dirty, reset, markSaved } = useDraft<ColorForm[]>(initial, (a, b) => plain(a) === plain(b));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const { toast, confirm } = useFeedback();
  useUnloadGuard(dirty);
  useEffect(() => onDirty(dirty), [dirty, onDirty]);

  const update = (uid: string, patch: Partial<ColorForm>) => setColors((list) => list.map((c) => (c.uid === uid ? { ...c, ...patch } : c)));

  const save = async () => {
    const submitted = colors;
    const bad = colors.find((c) => !c.label.trim() || !c.key || !HEX.test(c.hex));
    if (bad) {
      setError(new ApiError(400, t('Revisa el color «{name}»: nombre, clave y color #RRGGBB', { name: bad.label || t('sin nombre') })));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { model: saved } = await put<{ model: BikeModel }>(
        `/admin/models/${model.id}/colors`,
        colors.map((c) => ({
          key: c.key,
          label: c.label.trim(),
          hex: c.hex.toUpperCase(),
          sideRightMediaId: c.sideRight?.id ?? null,
          sideLeftMediaId: c.sideLeft?.id ?? null,
          frameIds: c.frames.map((f) => f.media.id),
        })),
      );
      onSaved(saved);
      markSaved(submitted);
      toast(t('Colores guardados'));
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, errorMessage(e)));
    } finally {
      setBusy(false);
    }
  };

  const add = () =>
    setColors((list) => [
      ...list,
      { uid: crypto.randomUUID(), key: '', keyTouched: false, label: '', hex: '#E21E28', sideRight: null, sideLeft: null, frames: [] },
    ]);

  return (
    <div className="stack">
      <div className="row between">
        <p className="muted" style={{ margin: 0, maxWidth: '70ch' }}>
          {t(
            'El primer color es el que se ve al entrar a la ficha y en el catálogo. Laterales y fotos 360 en PNG o WebP con fondo transparente; las 360, todas del mismo tamaño y en orden de giro.',
          )}
        </p>
        {colors.length < LIMITS.colors && (
          <button className="btn" onClick={add}>
            <IconPlus /> {t('Color')}
          </button>
        )}
      </div>

      <SortableList items={colors} getId={(c) => c.uid} onReorder={setColors} className="stack">
        {(c, i, handle) => (
          <section className="panel panel-pad stack">
            <div className="row row-wrap" style={{ alignItems: 'flex-start' }}>
              <FieldAligned>
                <DragHandle {...handle} />
              </FieldAligned>
              <Field label={i === 0 ? t('Color principal') : t('Nombre del color')}>
                {(id) => (
                  <input
                    id={id}
                    className="input"
                    value={c.label}
                    placeholder={t('Rojo')}
                    onChange={(e) => update(c.uid, { label: e.target.value, ...(!c.keyTouched && { key: slugify(e.target.value) }) })}
                  />
                )}
              </Field>
              <Field label={t('Clave')} hint={t('Va en las rutas de las fotos')}>
                {(id) => <input id={id} className="input mono" style={{ width: 150 }} value={c.key} onChange={(e) => update(c.uid, { key: slugify(e.target.value), keyTouched: true })} />}
              </Field>
              <Field label={t('Muestra')}>
                {(id) => (
                  <div className="color-input">
                    <input type="color" aria-label={t('Elegir color')} value={HEX.test(c.hex) ? c.hex : '#000000'} onChange={(e) => update(c.uid, { hex: e.target.value.toUpperCase() })} />
                    <input id={id} className="input mono" style={{ width: 110 }} value={c.hex} onChange={(e) => update(c.uid, { hex: e.target.value })} />
                  </div>
                )}
              </Field>
              <FieldAligned style={{ marginLeft: 'auto' }}>
                <button
                  className="btn btn-ghost"
                  onClick={async () => {
                    if (await confirm(t('¿Quitar el color «{name}» con sus fotos 360? Se aplica al guardar.', { name: c.label || t('sin nombre') }), { confirmLabel: t('Quitar'), danger: true })) {
                      setColors((list) => list.filter((x) => x.uid !== c.uid));
                    }
                  }}
                >
                  <IconTrash /> {t('Quitar')}
                </button>
              </FieldAligned>
            </div>

            <div className="grid grid-2">
              <ImageSlot label={t('Lateral derecho (mira a la derecha)')} media={c.sideRight} onChange={(m) => update(c.uid, { sideRight: m })} checker aspect="23 / 16" />
              <ImageSlot label={t('Lateral izquierdo (mira a la izquierda)')} media={c.sideLeft} onChange={(m) => update(c.uid, { sideLeft: m })} checker aspect="23 / 16" />
            </div>

            <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', alignItems: 'start' }}>
              <div className="field">
                <span className="field-label">
                  {t('Fotos 360')} <span className="counter">{tn(c.frames.length, '{n} fotos', '{n} fotos')}</span>
                </span>
                <ImageStrip
                  items={c.frames}
                  onChange={(frames) => update(c.uid, { frames })}
                  addLabel={t('Agregar fotos 360 (puedes elegir varias)')}
                  max={LIMITS.frames}
                  checker
                />
              </div>
              <div className="field">
                <span className="field-label">{t('Vista previa')}</span>
                {c.frames.length ? <SpinPreview frames={c.frames.map((f) => f.media)} /> : <div className="slot checker" style={{ aspectRatio: '3 / 2' }} />}
                <div className="hint">{t('Arrastra sobre la moto para comprobar el orden del giro.')}</div>
              </div>
            </div>
          </section>
        )}
      </SortableList>

      {colors.length === 0 && (
        <div className="panel empty-state">
          <span>{t('Esta moto todavía no tiene colores.')}</span>
          <button className="btn btn-primary" onClick={add}>
            <IconPlus /> {t('Agregar el primero')}
          </button>
        </div>
      )}

      <SaveBar dirty={dirty} busy={busy} error={error} onSave={save} onReset={reset} />
    </div>
  );
}
